import { useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { usersApi, type UserCreate } from '@/api/users'
import { useAuthStore, useEffectiveRole } from '@/store/auth'
import { toast } from '@/store/toast'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import Spinner from '@/components/common/Spinner'
import { format } from 'date-fns'
import { UserPlus, Send, Power, Trash2, ChevronRight, Search, ArrowUp, ArrowDown, RotateCcw } from 'lucide-react'
import type { Role, UserOut } from '@/types'
import HelpButton from '@/components/common/HelpButton'
import ConfirmModal from '@/components/common/ConfirmModal'
import TabBtn from '@/components/common/TabBtn'
import InviteAgainModal, { type DuplicateUserInfo } from '@/components/users/InviteAgainModal'

const ROLE_LABELS: Record<Role, string> = {
  director:          'Директор',
  deputy_director:   'Зам. директора',
  chief_engineer:    'Главный инженер проекта',
  designer:          'Проектировщик',
}

const schema = z.object({
  email: z.string().email('Корректный email'),
  role:  z.enum(['director', 'deputy_director', 'chief_engineer', 'designer']),
})
type FormData = z.infer<typeof schema>

type SortKey = 'full_name' | 'role' | 'last_login_at'
type SortDir = 'asc' | 'desc'

type ListTab = 'active' | 'deleted'

export default function UsersPage() {
  const [showForm, setShowForm] = useState(false)
  const [search, setSearch] = useState('')
  const [roleFilter, setRoleFilter] = useState<'' | Role>('')
  const [statusFilter, setStatusFilter] = useState<'' | 'active' | 'blocked'>('')
  const [sort, setSort] = useState<{ key: SortKey; dir: SortDir }>({ key: 'full_name', dir: 'asc' })
  const [tab, setTab] = useState<ListTab>('active')
  const [confirmHardDelete, setConfirmHardDelete] = useState<string | null>(null)
  const [duplicate, setDuplicate] = useState<{ email: string; info: DuplicateUserInfo } | null>(null)

  const qc = useQueryClient()
  const currentUser   = useAuthStore((s) => s.user)
  const effectiveRole = useEffectiveRole()
  const navigate = useNavigate()
  const isDirectorFamily = effectiveRole === 'director' || effectiveRole === 'deputy_director'

  const { data: users = [], isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersApi.list().then((r) => r.data),
  })

  const { data: deletedUsers = [], isLoading: loadingDeleted } = useQuery({
    queryKey: ['users-deleted'],
    queryFn: () => usersApi.listDeleted().then((r) => r.data),
    enabled: isDirectorFamily && tab === 'deleted',
  })

  const createMutation = useMutation({
    mutationFn: (data: UserCreate) => usersApi.create(data),
    onSuccess: (_res, vars) => {
      qc.invalidateQueries({ queryKey: ['users'] })
      setShowForm(false)
      reset()
      toast.success(`Приглашение отправлено на ${vars.email}`)
    },
    onError: (err: any, vars) => {
      const detail = err?.response?.data?.detail
      if (detail && typeof detail === 'object' && detail.code === 'user_exists') {
        setDuplicate({ email: vars.email, info: detail as DuplicateUserInfo })
      }
    },
  })

  const patchMutation = useMutation({
    mutationFn: ({ id, is_active }: { id: string; is_active: boolean }) =>
      usersApi.patch(id, { is_active }),
    onSuccess: (_res, vars) => {
      qc.invalidateQueries({ queryKey: ['users'] })
      toast.success(vars.is_active ? 'Доступ восстановлен' : 'Доступ заблокирован')
    },
  })

  const resendMutation = useMutation({
    mutationFn: (id: string) => usersApi.resendInvite(id),
    onSuccess: () => {
      toast.success('Приглашение отправлено повторно')
      setDuplicate(null)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => usersApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] })
      qc.invalidateQueries({ queryKey: ['users-deleted'] })
      toast.success('Сотрудник удалён')
    },
    onError: (err: any) =>
      toast.error(err?.response?.data?.detail ?? 'Не удалось удалить сотрудника'),
  })

  const restoreMutation = useMutation({
    mutationFn: (id: string) => usersApi.restore(id),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['users'] })
      qc.invalidateQueries({ queryKey: ['users-deleted'] })
      toast.success(`Сотрудник восстановлен, приглашение отправлено на ${res.data.email}`)
      setDuplicate(null)
      setShowForm(false)
      reset()
    },
  })

  const hardDeleteMutation = useMutation({
    mutationFn: (id: string) => usersApi.hardDelete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users-deleted'] })
      toast.success('Сотрудник удалён навсегда')
      setConfirmHardDelete(null)
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.detail ?? 'Не удалось удалить навсегда')
      setConfirmHardDelete(null)
    },
  })

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({ resolver: zodResolver(schema) })

  // ── Фильтрация + сортировка (клиентская — список загружен целиком) ──
  const visibleUsers = useMemo(() => {
    const q = search.trim().toLowerCase()
    let list = users.filter((u) => {
      if (q && !(`${u.full_name ?? ''} ${u.email}`.toLowerCase().includes(q))) return false
      if (roleFilter && u.role !== roleFilter) return false
      if (statusFilter === 'active' && !u.is_active) return false
      if (statusFilter === 'blocked' && u.is_active) return false
      return true
    })
    const dir = sort.dir === 'asc' ? 1 : -1
    list = [...list].sort((a, b) => {
      let av: string
      let bv: string
      if (sort.key === 'last_login_at') {
        av = a.last_login_at ?? ''
        bv = b.last_login_at ?? ''
      } else if (sort.key === 'role') {
        av = ROLE_LABELS[a.role]
        bv = ROLE_LABELS[b.role]
      } else {
        av = (a.full_name ?? '').toLowerCase()
        bv = (b.full_name ?? '').toLowerCase()
      }
      return av < bv ? -dir : av > bv ? dir : 0
    })
    return list
  }, [users, search, roleFilter, statusFilter, sort])

  if (isLoading) return <Spinner size="lg" />

  const isDirector = effectiveRole === 'director'

  function toggleSort(key: SortKey) {
    setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }))
  }

  function SortHeader({ label, sortKey }: { label: string; sortKey: SortKey }) {
    const active = sort.key === sortKey
    return (
      <th
        className="px-3 py-2 text-left font-medium text-gray-500 cursor-pointer select-none hover:text-gray-700"
        onClick={() => toggleSort(sortKey)}
      >
        <span className="inline-flex items-center gap-1">
          {label}
          {active && (sort.dir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
        </span>
      </th>
    )
  }

  function handleDelete(u: UserOut) {
    if (window.confirm(`Удалить сотрудника ${u.full_name ?? u.email}? Действие можно отменить только восстановлением из БД.`)) {
      deleteMutation.mutate(u.id)
    }
  }

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-gray-900">Сотрудники</h1>
        <div className="flex items-center gap-2">
          <HelpButton />
          {tab === 'active' && (
            <button
              data-tour="add-user-btn"
              onClick={() => setShowForm(!showForm)}
              className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-2 text-sm font-medium text-white hover:bg-brand-700 transition-colors"
            >
              <UserPlus className="h-4 w-4" />
              Добавить
            </button>
          )}
        </div>
      </div>

      {/* Вкладки */}
      {isDirectorFamily && (
        <div className="flex items-center gap-1 border-b border-gray-200">
          <TabBtn label="Сотрудники" count={users.length} active={tab === 'active'} onClick={() => setTab('active')} />
          <TabBtn label="Удалённые" count={deletedUsers.length} active={tab === 'deleted'} onClick={() => setTab('deleted')} danger />
        </div>
      )}

      {/* ── Таб «Удалённые» ──────────────────────────────────────────────── */}
      {tab === 'deleted' && (
        <div className="rounded-xl bg-white border border-gray-200 shadow-sm overflow-hidden">
          {loadingDeleted ? (
            <div className="p-8 flex justify-center"><Spinner /></div>
          ) : deletedUsers.length === 0 ? (
            <div className="p-8 text-center text-sm text-gray-400">Корзина пуста</div>
          ) : (
            <table className="min-w-full divide-y divide-gray-100 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-500">ФИО</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500">Email</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500">Роль</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500">Удалён</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {deletedUsers.map((u) => (
                  <tr key={u.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-700">
                      {u.full_name ?? <span className="text-gray-400 italic text-xs">Не заполнено</span>}
                    </td>
                    <td className="px-4 py-3 text-gray-500">{u.email}</td>
                    <td className="px-4 py-3 text-gray-500">{ROLE_LABELS[u.role]}</td>
                    <td className="px-4 py-3 text-xs text-gray-400">
                      {u.deleted_at ? format(new Date(u.deleted_at), 'dd.MM.yyyy') : '-'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => restoreMutation.mutate(u.id)}
                          disabled={restoreMutation.isPending}
                          className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-2.5 py-1 text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-50 transition-colors"
                        >
                          <RotateCcw className="h-3 w-3" />
                          Восстановить
                        </button>
                        <button
                          onClick={() => setConfirmHardDelete(u.id)}
                          className="inline-flex items-center gap-1 rounded-lg border border-rose-200 px-2.5 py-1 text-xs text-rose-600 hover:bg-rose-50 transition-colors"
                        >
                          <Trash2 className="h-3 w-3" />
                          Удалить навсегда
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Форма добавления */}
      {tab === 'active' && showForm && (
        <div className="rounded-xl bg-white border border-gray-200 shadow-sm p-5">
          <h2 className="mb-4 font-semibold text-gray-900">Новый сотрудник</h2>
          <p className="mb-4 text-sm text-gray-500">
            Сотрудник сам заполнит ФИО и телефон при первом входе.
          </p>
          <form
            onSubmit={handleSubmit((data) => createMutation.mutate(data))}
            className="grid grid-cols-1 md:grid-cols-2 gap-4"
          >
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                {...register('email')}
                className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
              {errors.email && <p className="mt-1 text-xs text-rose-600">{errors.email.message}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Роль</label>
              <select
                {...register('role')}
                className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              >
                {isDirector && (
                  <>
                    <option value="director">Директор (Администратор)</option>
                    <option value="deputy_director">Зам. директора</option>
                  </>
                )}
                <option value="chief_engineer">Главный инженер проекта</option>
                <option value="designer">Проектировщик</option>
              </select>
            </div>
            <div className="md:col-span-2 flex gap-3">
              <button
                type="submit"
                disabled={isSubmitting || createMutation.isPending}
                className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50 transition-colors"
              >
                {createMutation.isPending ? 'Сохранение...' : 'Пригласить'}
              </button>
              <button
                type="button"
                onClick={() => { setShowForm(false); reset() }}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Отмена
              </button>
            </div>
            {createMutation.isError && !duplicate && (
              <p className="md:col-span-2 text-sm text-rose-600">
                {(() => {
                  const detail = (createMutation.error as any)?.response?.data?.detail
                  return typeof detail === 'string' ? detail : 'Ошибка при создании'
                })()}
              </p>
            )}
          </form>
        </div>
      )}

      {/* Панель фильтров */}
      {tab === 'active' && (
      <>
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Поиск по ФИО или email"
            className="w-64 rounded-lg border border-gray-200 bg-white pl-8 pr-3 py-1.5 text-sm text-gray-700 focus:border-brand-500 focus:outline-none"
          />
        </div>
        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value as '' | Role)}
          className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 focus:border-brand-500 focus:outline-none"
        >
          <option value="">Все роли</option>
          <option value="director">Директор</option>
          <option value="deputy_director">Зам. директора</option>
          <option value="chief_engineer">Главный инженер проекта</option>
          <option value="designer">Проектировщик</option>
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as '' | 'active' | 'blocked')}
          className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 focus:border-brand-500 focus:outline-none"
        >
          <option value="">Все статусы</option>
          <option value="active">Активные</option>
          <option value="blocked">Заблокированные</option>
        </select>
        <span className="text-xs text-gray-400">Найдено: {visibleUsers.length}</span>
      </div>

      {/* Таблица пользователей */}
      <div className="rounded-xl bg-white border border-gray-200 shadow-sm overflow-hidden" data-tour="users-table">
        <table className="min-w-full divide-y divide-gray-100 text-[13px]">
          <thead className="bg-gray-50">
            <tr>
              <SortHeader label="ФИО" sortKey="full_name" />
              <th className="px-3 py-2 text-left font-medium text-gray-500">Email</th>
              <SortHeader label="Роль" sortKey="role" />
              <th className="px-3 py-2 text-left font-medium text-gray-500">Статус</th>
              <SortHeader label="Последний вход" sortKey="last_login_at" />
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {visibleUsers.map((u) => (
              <tr
                key={u.id}
                className={`hover:bg-blue-50/40 transition-colors cursor-pointer ${!u.is_active ? 'opacity-60' : ''}`}
                onClick={() => navigate(`/users/${u.id}`)}
              >
                <td className="px-3 py-1.5 font-medium">
                  <div className="flex items-center gap-1.5">
                    {u.full_name ?? (
                      <span className="text-gray-400 italic text-xs">Не заполнено</span>
                    )}
                    <ChevronRight className="h-3.5 w-3.5 text-gray-300 flex-shrink-0" />
                  </div>
                </td>
                <td className="px-3 py-1.5 text-gray-600">{u.email}</td>
                <td className="px-3 py-1.5 text-gray-600">{ROLE_LABELS[u.role]}</td>
                <td className="px-3 py-1.5">
                  <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                    u.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'
                  }`}>
                    {u.is_active ? 'Активен' : 'Заблокирован'}
                  </span>
                </td>
                <td className="px-3 py-1.5 text-gray-500 text-xs">
                  {u.last_login_at
                    ? format(new Date(u.last_login_at), 'dd.MM.yyyy HH:mm')
                    : 'Не входил'}
                </td>
                <td className="px-3 py-1.5 text-right" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-end gap-2">
                    {!u.last_login_at && u.is_active && (
                      <button
                        onClick={() => resendMutation.mutate(u.id)}
                        title="Повторно отправить приглашение"
                        className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-brand-600"
                      >
                        <Send className="h-3.5 w-3.5" />
                        Пригласить
                      </button>
                    )}
                    {u.id !== currentUser?.id && (
                      <>
                        <button
                          onClick={() => patchMutation.mutate({ id: u.id, is_active: !u.is_active })}
                          title={u.is_active ? 'Заблокировать доступ' : 'Восстановить доступ'}
                          className={`inline-flex items-center gap-1 text-xs ${
                            u.is_active
                              ? 'text-gray-400 hover:text-rose-500'
                              : 'text-gray-400 hover:text-emerald-600'
                          }`}
                        >
                          <Power className="h-3.5 w-3.5" />
                          {u.is_active ? 'Отключить' : 'Включить'}
                        </button>
                        <button
                          onClick={() => handleDelete(u)}
                          title="Удалить сотрудника"
                          className="inline-flex items-center text-xs text-gray-400 hover:text-rose-600"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {visibleUsers.length === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-8 text-center text-gray-400">
                  {users.length === 0 ? 'Пользователей нет' : 'Ничего не найдено по фильтрам'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      </>
      )}

      {duplicate && (
        <InviteAgainModal
          email={duplicate.email}
          duplicate={duplicate.info}
          onClose={() => setDuplicate(null)}
          onResendInvite={() => resendMutation.mutate(duplicate.info.user_id)}
          onRestore={() => restoreMutation.mutate(duplicate.info.user_id)}
          isPending={resendMutation.isPending || restoreMutation.isPending}
        />
      )}

      {confirmHardDelete && (
        <ConfirmModal
          title="Удалить навсегда?"
          text="Сотрудник будет удалён безвозвратно. Это действие нельзя отменить."
          confirmLabel="Удалить навсегда"
          danger
          onConfirm={() => hardDeleteMutation.mutate(confirmHardDelete)}
          onCancel={() => setConfirmHardDelete(null)}
          isPending={hardDeleteMutation.isPending}
        />
      )}
    </div>
  )
}
